/**
 * AppShell — fallback shown while the editor view's lazy chunk loads.
 *
 * Used by the LazyLoad in App.tsx (when `loaded === false`) and rendered
 * in place of the actual editor until the chunk arrives. We use the same
 * design-system <LoadingScreen> as the initial app boot so the user sees
 * one consistent loading visual instead of two different ones.
 *
 * The legacy version of this file rendered an entire skeleton chrome
 * (toolbar + sidebar + editor + footer placeholders). That was useful
 * when the chunk took seconds and reproducing the full shape gave users
 * something to anchor to; today the chunk loads in <500ms on cached
 * visits so the placeholder chrome added more visual noise than it
 * removed. The single LoadingScreen overlay is enough.
 */
import React, { CSSProperties } from 'react';

import { LoadingScreen } from '@/shared/components/loading-screen/LoadingScreen';

type Props = {
  /** Unused — accepted for API parity with the legacy shell. The new
   *  LoadingScreen has its own caption. */
  title?: string | null;
  /** Unused — accepted for API parity. The legacy shell rendered a
   *  preview placeholder when `previewShown === true`. */
  previewShown?: boolean;
};

export default function AppShell(_props: Props) {
  return (
    <div className="tile-loading-host" style={hostStyle}>
      <LoadingScreen visible caption="Loading Tile" />
    </div>
  );
}

// Inline — must fill the page so the absolutely-positioned `.skeleton`
// overlay covers everything behind it.
const hostStyle: CSSProperties = {
  position: 'absolute',
  inset: 0,
  width: '100%',
  height: '100%',
};
